/**
 * What armour takes off a hit.
 *
 * Two halves, in this order. The threshold is flat: armour that stops a pistol round stops it
 * whatever else is going on, which is what makes a small calibre feel wrong against a plate. The
 * rating is proportional and has diminishing returns, so stacking it is always worth something and
 * never worth everything.
 *
 * The cap is the load-bearing part. Without it, enough armour reaches total immunity, and an enemy
 * that cannot be hurt is a bug that reads as a design decision.
 */

import { statValue, THRESHOLD_PIERCE_PER_PERCEPTION } from '@/sim/combat/statContract';
import type { StatBlock } from '@/sim/combat/statContract';

export interface ArmorProfile {
  /** Flat damage stopped outright, before the rating applies. */
  readonly threshold: number;
  /** Proportional protection, fed through the diminishing returns curve. */
  readonly rating: number;
}

export const NO_ARMOR: ArmorProfile = { threshold: 0, rating: 0 };

/** Most a rating can ever take off. Nothing is immune. */
export const DAMAGE_REDUCTION_CAP = 0.85;

/** Rating at which the curve gives half of what it eventually could. */
export const DAMAGE_REDUCTION_HALF_POINT = 60;

/**
 * Applies armour to `damage`.
 *
 * `efficiency` is how much of the profile counts against this hit, the damage type's
 * `armorEfficiency` times the hit location's `armorCoverage`. An attacker's Perception shaves the
 * threshold, because a shooter who reads the target aims where the plate is not.
 */
export function mitigate(
  damage: number,
  armor: ArmorProfile,
  efficiency: number,
  attacker: StatBlock | null,
): number {
  const share = clampUnit(efficiency);
  if (share === 0) return Math.max(0, damage);

  const threshold = Math.max(0, armor.threshold) * share * (1 - thresholdPierce(attacker));
  const afterThreshold = Math.max(0, damage - threshold);

  const rating = Math.max(0, armor.rating) * share;
  return afterThreshold * (1 - reductionFor(rating));
}

/** Fraction of the incoming damage a rating removes, capped below total immunity. */
export function reductionFor(rating: number): number {
  if (!Number.isFinite(rating) || rating <= 0) return 0;
  const curve = rating / (rating + DAMAGE_REDUCTION_HALF_POINT);
  return Math.min(DAMAGE_REDUCTION_CAP, curve);
}

function thresholdPierce(attacker: StatBlock | null): number {
  if (attacker === null) return 0;
  const perception = statValue(attacker, 'perception');
  return clampUnit(THRESHOLD_PIERCE_PER_PERCEPTION * perception);
}

function clampUnit(value: number): number {
  if (!Number.isFinite(value)) return 0;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}
